import { useCallback, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import { jwtPayload } from "atoms/auth";
import { useQuery } from "@tanstack/react-query";
import { useQueryString } from "./useQueryString";
import { useRecoilState } from "recoil";
import { getSubjectIdForProgramInstance } from "api/services/programService";
import { exchangeEmailForToken } from "api/services/authService";
import { getAdfenixToken, parseJwt } from "utils/authHelpers";
import { useParams } from "react-router-dom";

export const useAdfenixToken = () => {
  const [payload, setPayload] = useRecoilState(jwtPayload);

  const { userId, legacyToken, guid, programInstanceId } = useQueryString();
  const { guid: guidFromParam } = useParams();

  const [propertyId, setPropertyId] = useState<string | undefined>(
    guid ?? guidFromParam
  );
  const [token] = useState<string | undefined>(legacyToken);

  const [parsedToken, setParsedToken] = useState(false);

  const { isInitialLoading: isLoadingSubjectId, data } = useQuery(
    ["getSubjectIdForProgramInstance"],
    () => getSubjectIdForProgramInstance(programInstanceId ?? ""),
    {
      enabled: Boolean((userId || token) && programInstanceId)
    }
  );

  useEffect(() => {
    if (data) {
      setPropertyId(data);
    }
  }, [data, propertyId]);

  const { data: adfenixTokenData, isInitialLoading: isLoading } = useQuery(
    [
      "exchangeEmailForToken",
      {
        propertyId: propertyId,
        type: userId ? "UserId" : "LegacyToken",
        userAccess: userId ?? token
      }
    ],
    () =>
      exchangeEmailForToken(
        propertyId,
        userId ? "UserId" : "LegacyToken",
        userId ?? token
      ),
    {
      enabled: Boolean((userId || token) && propertyId),
      refetchOnWindowFocus: false,
      retry: false
    }
  );

  const [isTokenExpired, setIsTokenExpired] = useState(false);

  const now = useMemo(dayjs, []); // useMemo(() => dayjs,[])
  const tokenExpiryDate = useMemo(
    () => dayjs(adfenixTokenData?.expiryDate),
    [adfenixTokenData?.expiryDate]
  );

  useEffect(() => {
    if (tokenExpiryDate.isBefore(now)) {
      setIsTokenExpired(true);
    }
  }, [now, tokenExpiryDate]);

  const [usesAdfenixToken, setUsesAdfenixToken] = useState(
    Boolean(getAdfenixToken()) || Boolean(propertyId && (userId || token))
  );

  useEffect(() => {
    if (isLoading || isLoadingSubjectId) {
      setUsesAdfenixToken(true);
    }
  }, [isLoading, isLoadingSubjectId]);

  const handleToken = useCallback(() => {
    const base64Token = getAdfenixToken();

    if (base64Token && !payload) {
      setUsesAdfenixToken(true);
      const payload = parseJwt(base64Token);

      setPayload(payload);
      setParsedToken(true);

      return payload;
    }
  }, [payload, setPayload]);

  useEffect(() => {
    if (adfenixTokenData) {
      Cookies.set(adfenixTokenData.key, adfenixTokenData.value, {
        expires: new Date(adfenixTokenData.expiryDate),
        sameSite: "lax",
        secure: process.env.REACT_APP_COGNITO_COOKIE_SECURE === "true"
      });
    }

    if (!isLoading && !isLoadingSubjectId) handleToken();
  }, [
    adfenixTokenData,
    handleToken,
    isLoading,
    parsedToken,
    isLoadingSubjectId
  ]);

  return {
    isLoadingToken: isLoadingSubjectId || isLoading,
    isTokenExpired,
    usesAdfenixToken,
    fetchedAdfenixToken: usesAdfenixToken && payload
  };
};
